import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";
import { useFontSize } from "../FontSizeContext";

// export const ResumePDFSection = ({
//   themeColor,
//   heading,
//   style = {},
//   children,
// }: {
//   themeColor?: string;
//   heading?: string;
//   style?: Style;
//   children: React.ReactNode;
// }) => {
//   const handleClickCallback = useCallback(
//     () => handleClick(heading),
//     [heading]
//   );
//   return (
//     <div
//       onClick={handleClickCallback}
//       style={{
//         ...styles.flexCol,
//         gap: spacing["2"],
//         marginTop: spacing["5"],
//         cursor: "pointer",
//       }}
//     >
//         {heading && (
//           <View
//           minPresenceAhead={300}
//             style={{ ...styles.flexCol, gap: spacing["1"] }}
//             // minPresenceAhead={120}
//           >
//             <Text
//               style={{
//                 fontWeight: "bold",
//                 letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
//                 color: themeColor || DEFAULT_FONT_COLOR,
//               }}
//               debug={DEBUG_RESUME_PDF_FLAG}
//             >
//               {heading}
//             </Text>
//             {themeColor && (
//               <View
//                 style={{
//                   height: "1.25pt",
//                   width: "100%",
//                   backgroundColor: "black",
//                   marginRight: spacing["3.5"],
//                 }}
//                 debug={DEBUG_RESUME_PDF_FLAG}
//               />
//             )}
//           </View>
//         )}
//         {children}
      
//     </div>
//   );
// };
export const ResumePDFSection = ({
  themeColor,
  heading,
  isSectionSelected,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  isSectionSelected?: boolean;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      <View
        wrap={false}
        style={{
          ...styles.flexCol,
          gap: spacing["1.5"], // Reduced gap for better space utilization
          marginTop: spacing["3"], // Reduced from spacing[5] for more content per page
          // cursor: "pointer",
        }}
      >
        {heading && (
          <View
            style={{ ...styles.flexCol, gap: spacing["0.5"] }} // Tighter gap for headings
            // minPresenceAhead={120}
          >
            <Text
              style={{
                ...dynamicStyles.heading, // Use dynamic heading styles with fontSize
                letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "black",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
        )}
        {children}
      </View>
    </div>
  );
};

export const ResumePDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  
  const childrenArray = React.Children.toArray(children);
  const firstChild = childrenArray[0];
  const restChildren = childrenArray.slice(1);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            marginTop: spacing["3"], // Reduced from spacing[5] for better page utilization
          }}
          wrap={false}
        >
          {/* Heading */}
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"],
            }}
          >
            <Text
              style={{
                ...dynamicStyles.heading, // Use dynamic heading styles with fontSize
                letterSpacing: "0.3pt", 
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
            {themeColor && (
              <View
                style={{
                  height: "1.25pt",
                  width: "100%",
                  backgroundColor: "black",
                  marginRight: spacing["3.5"],
                }}
                debug={DEBUG_RESUME_PDF_FLAG}
              />
            )}
          </View>
          {/* First child */}
          <View
            style={{
              marginTop: spacing["1.5"], // Reduced gap between heading and content
            }}
          >
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["0.5"], // Tighter gap for headings
            marginTop: spacing["3"], // Reduced from spacing[5] for better space utilization
          }}
          // minPresenceAhead={300}
        >
          <Text
            style={{
              ...styles.heading, // Apply consistent heading styles
              letterSpacing: "0.3pt", 
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "1.25pt",
                width: "100%",
                backgroundColor: "black",
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      
      {/* Rest of children */}
      {restChildren.length > 0 && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"], // Reduced gap between subsections for better space utilization
          }}
        >
          {restChildren}
        </View>
      )}
    </div>
  );
};

export const ResumePDFText = ({
  bold = false,
  italic = false,
  underline = false,
  themeColor,
  style = {},
  children,
}: {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  themeColor?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const { dynamicStyles } = useFontSize();
  return (
    <Text
      style={{
        ...dynamicStyles.text, // Use dynamic text styles with fontSize
        color: themeColor || DEFAULT_FONT_COLOR,
        fontWeight: bold ? "bold" : "normal",
        fontStyle: italic ? "italic" : "normal",
        textDecoration: underline ? "underline" : "none",
        ...style, // Allow style overrides
      }}
      debug={DEBUG_RESUME_PDF_FLAG}
    >
      {children}
    </Text>
  );
};

export const ResumePDFBulletList = ({
  items,
  showBulletPoints = true,
}: {
  items: string[];
  showBulletPoints?: boolean;
}) => {
  return (
    <>
      {items.map((item, idx) => (
        <View style={{ ...styles.flexRow }} key={idx}>
          {showBulletPoints && (
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
              }}
              bold={true}
            >
              {"•"}
            </ResumePDFText>
          )}
          {/* A breaking change was introduced causing text layout to be wider than node's width
              https://github.com/diegomura/react-pdf/issues/2182. flexGrow & flexBasis fixes it */}
          <ResumePDFText
            style={{ flexGrow: 1, flexBasis: 0 }}
          >
            {item}
          </ResumePDFText>
        </View>
      ))}
    </>
  );
};

export const ResumePDFLink = ({
  src,
  isPDF,
  children,
}: {
  src: string;
  isPDF: boolean;
  children: React.ReactNode;
}) => {
  if (isPDF) {
    return (
      <Link src={src} style={{ textDecoration: "none" }}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={src}
      style={{ textDecoration: "none" }}
      target="_blank"
      rel="noreferrer"
    >
      {children}
    </a>
  );
};

export const ProjectPDFLink = ({
  src,
  children,
}: {
  src: string;
  children: React.ReactNode;
}) => {
  return (
    <Link src={src} style={{ display: "flex", textDecoration: "none" }}>
      <a href={src} target="_blank">
        {children}
      </a>
    </Link>
  );
};

export const ResumeFeaturedSkill = ({
  skill,
  rating,
  themeColor,
  style = {},
}: {
  skill: string;
  rating: number;
  themeColor: string;
  style?: Style;
}) => {
  const numCircles = 5;

  return (
    <View style={{ ...styles.flexRow, alignItems: "center", ...style }}>
      <ResumePDFText style={{ marginRight: spacing[0.5] }}>
        {skill}
      </ResumePDFText>
      {[...Array(numCircles)].map((_, idx) => (
        <View
          key={idx}
          style={{
            height: "9pt",
            width: "9pt",
            marginLeft: "2.25pt",
            backgroundColor: rating >= idx ? themeColor : "#d9d9d9",
            borderRadius: "100%",
          }}
        />
      ))}
    </View>
  );
};

export const ResumePDFSkillsTwoColumn = ({ items }: { items: string[] }) => {
  const allSkills = items.join("");
  const skillNodes = extractSkillNodesFromHtml(allSkills);
  const midPoint = Math.ceil(skillNodes.length / 2);
  const firstColumn = skillNodes.slice(0, midPoint);
  const secondColumn = skillNodes.slice(midPoint);

  return (
    <View style={{ ...styles.flexRowBetween }}>
      <View style={{ ...styles.flexCol, width: "48.5%" }}>
        {firstColumn.map((node, idx) => (
          <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
        ))}
      </View>
      <View style={{ ...styles.flexCol, width: "48.5%" }}>
        {secondColumn.map((node, idx) => (
          <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
        ))}
      </View>
    </View>
  );
};

export const extractSkillNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes: ChildNode[] = [];

  function traverse(node: ChildNode) {
    if (node.nodeName === "P" || node.nodeName === "LI") {
      nodes.push(node);
    }

    node.childNodes.forEach(traverse);
  }

  doc.body.childNodes.forEach(traverse);

  return nodes;
};

export const ResumePDFDescription = ({ items }: { items: string[] }) => {
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const description = items[0];
  const newDescription = items.join("");
  // console.log("newDescription in pdf desc..", newDescription);
  // const descriptionNodes = extractNodesFromHtml(description);
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>{renderNode(node)}</React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText key={Math.random()} underline={true}>
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          // return <Text key={Math.random()}>{element.textContent}</Text>;
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        // return <Text key={Math.random()}>{element.textContent}</Text>;
        return (
          <View>
            <ResumePDFText style={{ flexGrow: 1 }}>
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
      case "ol":
        return (
          <View key={Math.random()} style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                    }}
                    bold={true}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ flexGrow: 1, flexBasis: 0 }}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
      case "li":
        return (
          <View key={Math.random()} style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"], // adjust spacing as needed
                paddingRight: spacing["2"], // adjust spacing as needed
              }}
              bold={true}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                flexGrow: 1,
                flexBasis: 0,
              }}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
      default:
        return null;
    }
  }
  return null;
};

export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};
